// src/lib/auth/isCentralMode.ts
//
// H-2C2 — canonical "central mode" predicate.
//
// Two H-2C2 stabilization PRs (#23, #24) and later PR #26 each fixed a bug
// caused by drifting this predicate. The original failure mode: a single
// site checked only `authSource === 'central'`, which is `null` for RP/BFF
// users (their Central identity lives server-side via the `crm_sid` cookie;
// no browser-side token populates `centralAuth.user`). Code that read
// `authSource` alone treated RP users as legacy — broke onboarding, broke
// AppLayout, broke the SuiteShell.
//
// The fix that stuck was: ALWAYS check both — `AUTH_MODE === 'central'`
// (env-var fallback) OR `authSource === 'central'` (in-Redux signal). This
// file is the single source of truth for that predicate. Every call site
// MUST import from here; inline duplicates are caught by a source-level
// regression guard in `src/__tests__/lib/auth/isCentralMode.test.ts`.
//
// Pure function — no React, no Redux, no side effects. Trivially testable.

/**
 * Returns true when the app is in Central mode for ANY of the H-2C2-relevant
 * runtime paths (legacy Central-direct OR RP/BFF). Returns false for
 * pure-legacy mode.
 *
 * @param authMode   value of `AUTH_MODE` env var (typically from
 *                   `frontend/src/config/centralAuth.ts`). Accepts any
 *                   string; only `'central'` matches.
 * @param authSource value of `useAuth().authSource`. May be `'central'`,
 *                   `'legacy'`, or `null` (RP users before any Central
 *                   stripe loads).
 */
export function isCentralMode(
  authMode: string | undefined | null,
  authSource: string | undefined | null,
): boolean {
  return authMode === 'central' || authSource === 'central';
}
